import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, Loader2, AlertTriangle, Info, ChevronDown, ChevronRight,
  Send, Download, FileCheck, MessageSquarePlus, Eye, EyeOff, Sparkles, Link2,
} from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { EXT_STATUS } from "@/pages/ExternalQuestionnaires";

const ANSWER_BADGE = {
  PREFILLED: { label: "GIÀ DISPONIBILE", cls: "bg-green-100 text-green-800 border-green-300" },
  CONFIRMED: { label: "Confermato", cls: "bg-green-700 text-white border-green-700" },
  CONSULTANT_VERIFIED: { label: "Verificato dal consulente", cls: "bg-green-700 text-white border-green-700" },
  APPROVED: { label: "Approvato", cls: "bg-green-700 text-white border-green-700" },
  MISSING: { label: "DA COMPLETARE", cls: "bg-red-100 text-red-800 border-red-300" },
  TO_VERIFY: { label: "DA VERIFICARE", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  CONFLICT: { label: "CONFLITTO", cls: "bg-red-600 text-white border-red-600" },
};

function AnswerInput({ question, value, disabled, onSave, testid }) {
  const [local, setLocal] = useState(value ?? "");
  const [saved, setSaved] = useState(false);
  useEffect(() => { setLocal(value ?? ""); }, [value]);
  const commit = async (v) => {
    await onSave(v === "" ? null : v);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };
  const dt = question.data_type;
  const cls = "w-full h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-600 disabled:bg-slate-50";
  const feedback = saved && (
    <span className="text-xs text-green-700 font-medium" data-testid={`${testid}-saved`}>Salvato.</span>
  );
  if (dt === "BOOLEAN" || dt === "YES_NO_UNKNOWN") {
    return (
      <div className="flex items-center gap-2">
        <select value={local === "" ? "" : String(local)} disabled={disabled}
          onChange={(e) => {
            const v = e.target.value;
            setLocal(v);
            commit(v === "" ? null : v === "UNKNOWN" ? "UNKNOWN" : v === "NOT_APPLICABLE" ? "NOT_APPLICABLE" : v === "true");
          }}
          className={cls} data-testid={testid} aria-label="Risposta">
          <option value="">—</option>
          <option value="true">Sì</option>
          <option value="false">No</option>
          {dt === "YES_NO_UNKNOWN" && question.allow_unknown && (
            <option value="UNKNOWN">Non so</option>)}
          {question.allow_not_applicable && (
            <option value="NOT_APPLICABLE">Non applicabile</option>)}
        </select>
        {feedback}
      </div>
    );
  }
  if (dt === "ENUM" && (question.options || []).length) {
    return (
      <div className="flex items-center gap-2">
        <select value={local} disabled={disabled} className={cls} data-testid={testid}
          onChange={(e) => { setLocal(e.target.value); commit(e.target.value); }}
          aria-label="Risposta">
          <option value="">—</option>
          {question.options.map((o) => (
            <option key={o} value={o}>{o}</option>))}
        </select>
        {feedback}
      </div>
    );
  }
  if (dt === "LONG_TEXT") {
    return (
      <div className="space-y-1">
        <textarea value={local} disabled={disabled} rows={3}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={() => commit(local)}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-600 disabled:bg-slate-50"
          data-testid={testid} aria-label="Risposta" />
        {feedback}
      </div>
    );
  }
  const numeric = ["INTEGER", "DECIMAL", "PERCENTAGE", "CURRENCY"].includes(dt);
  return (
    <div className="flex items-center gap-2">
      <input type={dt === "DATE" ? "date" : numeric ? "number" : "text"}
        value={local} disabled={disabled}
        step={dt === "INTEGER" ? "1" : "any"}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => commit(numeric && local !== "" ? Number(String(local).replace(",", ".")) : local)}
        className={cls} data-testid={testid} aria-label="Risposta" />
      {question.unit && <span className="text-xs text-slate-500 shrink-0">{question.unit}</span>}
      {feedback}
    </div>
  );
}

function QuestionCard({ question, answer, editable, consultantView, onAction, busy }) {
  const [showOriginal, setShowOriginal] = useState(false);
  const [showLineage, setShowLineage] = useState(null);
  const status = answer?.answer_status || "MISSING";
  const badge = ANSWER_BADGE[status] || ANSWER_BADGE.MISSING;
  const isConflict = status === "CONFLICT";
  const stale = answer?.stale;

  const openLineage = async () => {
    if (showLineage) { setShowLineage(null); return; }
    setShowLineage("loading");
    try {
      const { data } = await onAction("lineage", question);
      setShowLineage(data);
    } catch {
      setShowLineage(null);
    }
  };

  return (
    <Card className={`border ${isConflict ? "border-red-300 bg-red-50/40" : stale ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`}
      data-testid={`question-card-${question.qid}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-xs font-mono text-slate-400 mt-0.5">{question.qid}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 leading-relaxed max-w-prose">
              {question.friendly_text || question.original_text}
            </p>
            <button type="button"
              className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mt-0.5"
              onClick={() => setShowOriginal(!showOriginal)}
              data-testid={`toggle-original-${question.qid}`}>
              {showOriginal ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              Testo originale del destinatario
            </button>
            {showOriginal && (
              <p className="text-xs text-slate-600 italic mt-1 border-l-2 border-slate-200 pl-2"
                data-testid={`original-text-${question.qid}`}>
                “{question.original_text}”
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {question.required && status === "MISSING" && (
              <Badge variant="outline" className="text-[10px] border-red-300 text-red-700">Obbligatoria</Badge>)}
            <Badge className={`text-[10px] border ${badge.cls}`}
              data-testid={`answer-badge-${question.qid}`}>{badge.label}</Badge>
          </div>
        </div>

        {answer?.source_label && answer?.prefilled && (
          <p className="text-xs text-green-800 inline-flex items-center gap-1"
            data-testid={`source-label-${question.qid}`}>
            <Sparkles className="w-3 h-3" aria-hidden="true" />
            Già compilato da ESG-PMI — {answer.source_label}
            {answer.mapping_quality === "PROVISIONAL" && " (mapping da verificare)"}
          </p>
        )}

        {isConflict && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 space-y-2"
            data-testid={`conflict-box-${question.qid}`}>
            <p className="text-xs text-red-900">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" aria-hidden="true" />
              ESG-PMI contiene <strong>{String(answer.conflict_canonical_value)}</strong> per questo
              dato. Nel questionario hai indicato <strong>{String(answer.answer_value)}</strong>.
            </p>
            {editable && (
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="h-7 text-xs"
                  disabled={busy}
                  onClick={() => onAction("use-canonical", question, answer.conflict_canonical_value)}
                  data-testid={`conflict-use-canonical-${question.qid}`}>
                  Usa {String(answer.conflict_canonical_value)} (dato ESG)
                </Button>
                {consultantView && (
                  <Button size="sm" variant="outline"
                    className="h-7 text-xs text-green-800 border-green-300"
                    disabled={busy} onClick={() => onAction("verify", question)}
                    data-testid={`conflict-keep-${question.qid}`}>
                    Mantieni {String(answer.answer_value)} per questo questionario (verifica consulente)
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {stale && !isConflict && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2"
            data-testid={`stale-box-${question.qid}`}>
            <p className="text-xs text-amber-900">
              È disponibile un dato ESG più recente per questa risposta.
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
                onClick={() => onAction("resolve-stale", question, true)}
                data-testid={`stale-use-updated-${question.qid}`}>Usa dato aggiornato</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy}
                onClick={() => onAction("resolve-stale", question, false)}
                data-testid={`stale-keep-${question.qid}`}>Mantieni risposta</Button>
            </div>
          </div>
        )}

        <AnswerInput question={question} value={answer?.answer_value}
          disabled={!editable || busy}
          onSave={(v) => onAction("save", question, v)}
          testid={`answer-input-${question.qid}`} />

        <div className="flex items-center gap-2 flex-wrap">
          {editable && ["PREFILLED", "TO_VERIFY"].includes(status) && (
            <Button size="sm" variant="outline"
              className="h-7 text-xs text-green-800 border-green-300 hover:bg-green-50"
              disabled={busy} onClick={() => onAction("confirm", question)}
              data-testid={`confirm-answer-${question.qid}`}>
              <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Conferma dato
            </Button>
          )}
          {consultantView && ["CONFIRMED", "TO_VERIFY", "PREFILLED"].includes(status) && (
            <Button size="sm" variant="outline" className="h-7 text-xs"
              disabled={busy} onClick={() => onAction("verify", question)}
              data-testid={`verify-answer-${question.qid}`}>
              Verifica (consulente)
            </Button>
          )}
          {consultantView && (
            <Button size="sm" variant="ghost" className="h-7 text-xs"
              onClick={openLineage} data-testid={`lineage-btn-${question.qid}`}>
              <Link2 className="w-3 h-3 mr-1" aria-hidden="true" />Lineage
            </Button>
          )}
        </div>

        {showLineage && showLineage !== "loading" && (
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs space-y-1"
            data-testid={`lineage-panel-${question.qid}`}>
            <p><strong>Mapping:</strong> {showLineage.mapping
              ? `${showLineage.mapping.mapping_type} → ${showLineage.mapping.canonical_datapoint_code} (${showLineage.mapping.mapping_quality})`
              : "nessun mapping canonico"}</p>
            {showLineage.canonical_value && (
              <p><strong>Valore canonico:</strong> stato {showLineage.canonical_value.status},
                aggiornato il {showLineage.canonical_value.updated_at
                  ? new Date(showLineage.canonical_value.updated_at).toLocaleDateString("it-IT") : "—"}</p>
            )}
            {(showLineage.evidence || []).length > 0 && (
              <p><strong>Evidenze:</strong> {showLineage.evidence.map((e) =>
                `${e.document?.name || "documento"}${e.page ? ` p.${e.page}` : ""}`).join(", ")}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ExternalQuestionnaireDetail() {
  const { instanceId } = useParams();
  const { companyId, period, can } = useTenant();
  const navigate = useNavigate();
  const consultantView = can("externalq:review");
  const canApprove = can("externalq:approve");
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [focusMode, setFocusMode] = useState(true);
  const [openSections, setOpenSections] = useState({});
  const [integrationComment, setIntegrationComment] = useState("");
  const [showIntegration, setShowIntegration] = useState(false);

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(
        `/companies/${companyId}/periods/${period.id}/external-questionnaires/${instanceId}`);
      setDetail(data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [companyId, period?.id, instanceId]);

  useEffect(() => { load(); }, [load]);

  const answersByQ = useMemo(() => {
    const m = {};
    (detail?.answers || []).forEach((a) => { m[a.question_id] = a; });
    return m;
  }, [detail]);

  const base = `/companies/${companyId}/periods/${period?.id}/external-questionnaires/${instanceId}`;

  const action = async (type, question, payload) => {
    setBusy(true);
    try {
      if (type === "save") {
        const { data } = await api.put(`${base}/answers/${question.qid}`, { value: payload });
        if (data.answer_status === "CONFLICT") {
          toast.warning("Valore diverso dal dato ESG: è stato creato un conflitto da risolvere");
        }
      } else if (type === "confirm") {
        await api.post(`${base}/answers/${question.qid}/confirm`);
      } else if (type === "verify") {
        await api.post(`${base}/answers/${question.qid}/verify`);
        toast.success("Risposta verificata");
      } else if (type === "use-canonical") {
        await api.put(`${base}/answers/${question.qid}`, { value: payload });
        toast.success("Ripristinato il dato ESG");
      } else if (type === "resolve-stale") {
        await api.post(`${base}/answers/${question.qid}/resolve-stale`,
          { use_updated: payload });
      } else if (type === "lineage") {
        const r = await api.get(`${base}/lineage/${question.qid}`);
        return r.data;
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
    return null;
  };

  const workflow = async (kind) => {
    setBusy(true);
    try {
      if (kind === "submit") {
        const { data } = await api.post(`${base}/submit`);
        toast.success(data.warnings?.to_verify || data.warnings?.conflicts
          ? `Inviato al consulente con ${data.warnings.to_verify + data.warnings.conflicts} avvisi`
          : "Questionario inviato al consulente");
      } else if (kind === "START_REVIEW" || kind === "READY_FOR_APPROVAL") {
        await api.post(`${base}/review`, { action: kind });
        toast.success(kind === "START_REVIEW" ? "Revisione avviata" : "Pronto per approvazione");
      } else if (kind === "REQUEST_INTEGRATION") {
        await api.post(`${base}/review`, { action: kind, comment: integrationComment });
        toast.success("Richiesta di integrazione inviata all'azienda");
        setShowIntegration(false);
        setIntegrationComment("");
      } else if (kind === "approve") {
        await api.post(`${base}/approve`);
        toast.success("Questionario approvato: snapshot creato, contenuto bloccato");
      } else if (kind === "new-version") {
        const { data } = await api.post(`${base}/new-version`);
        toast.success("Nuova versione creata con i dati aggiornati");
        navigate(`/questionari-esterni/${data.id}`);
        return;
      }
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" && d?.message ? d.message : formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doExport = async (format, final) => {
    try {
      const r = await api.get(`${base}/export`, {
        params: { format, final }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${detail.template.code}_${period.year}.${format.toLowerCase()}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${format} ${final ? "FINALE" : "bozza"} scaricato`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (error) {
    return (
      <div className="max-w-lg mx-auto mt-16" data-testid="extq-detail-error">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center text-sm text-red-700">{error}</CardContent>
        </Card>
      </div>
    );
  }
  if (!detail) return <p className="text-sm text-slate-500">Caricamento…</p>;

  const { instance: inst, template, provider, stats } = detail;
  const st = EXT_STATUS[inst.status] || EXT_STATUS.RECEIVED;
  const editable = ["RECEIVED", "ANALYZING", "READY_FOR_COMPLETION", "IN_PROGRESS",
    "INTEGRATION_REQUESTED"].includes(inst.status);
  const clientEditable = editable && !consultantView;
  const consultantEditable = editable && consultantView;

  const gapOf = (q) => {
    const a = answersByQ[q.qid];
    if (a?.answer_status === "CONFLICT") return "CONFLICT";
    const v = a?.answer_value;
    if (v === "NOT_APPLICABLE") return "NOT_APPLICABLE";
    if (v === null || v === undefined || v === "") return "MISSING";
    if (a?.answer_status === "TO_VERIFY" || a?.stale) return "TO_VERIFY";
    return "ANSWERED";
  };
  const questions = template.questions || [];
  const focusQuestions = questions.filter((q) =>
    ["MISSING", "TO_VERIFY", "CONFLICT"].includes(gapOf(q)));

  return (
    <div className="space-y-6" data-testid="extq-detail-page">
      <div className="flex items-start gap-3 flex-wrap">
        <Button variant="ghost" size="sm" className="h-8"
          onClick={() => navigate("/questionari-esterni")} data-testid="extq-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" aria-hidden="true" />Questionari
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">
            {template.name}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {provider?.name}{inst.recipient_name && ` · ${inst.recipient_name}`}
            {inst.due_date && ` · Scadenza ${new Date(inst.due_date).toLocaleDateString("it-IT")}`}
            {" · Esercizio "}{period?.year}
          </p>
        </div>
        <Badge className={st.cls} data-testid="extq-detail-status">{st.label}</Badge>
      </div>

      <Card className="border-green-200 bg-green-50/60" data-testid="extq-progress-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-6 flex-wrap">
            <div>
              <p className="text-3xl font-extrabold text-green-800" data-testid="extq-answered">
                {stats.answered}<span className="text-base text-slate-500">/{stats.total_questions}</span>
              </p>
              <p className="text-xs text-slate-600">risposte pronte</p>
            </div>
            <div>
              <p className="text-3xl font-extrabold text-slate-900" data-testid="extq-prefill-pct">
                {stats.prefill_percentage}%
              </p>
              <p className="text-xs text-slate-600">già compilato da ESG-PMI</p>
            </div>
            <div>
              <p className="text-3xl font-extrabold text-red-700" data-testid="extq-missing">
                {stats.missing}
              </p>
              <p className="text-xs text-slate-600">da completare</p>
            </div>
            <div>
              <p className="text-3xl font-extrabold text-amber-700" data-testid="extq-toverify">
                {stats.to_verify}
              </p>
              <p className="text-xs text-slate-600">da verificare</p>
            </div>
            <div className="flex-1" />
            {!consultantView && editable && (
              <Button className="bg-green-700 hover:bg-green-800 text-white"
                disabled={busy} onClick={() => workflow("submit")}
                data-testid="submit-to-consultant-btn">
                <Send className="w-4 h-4 mr-1.5" aria-hidden="true" />
                {stats.missing > 0
                  ? `Completa le ${stats.missing + stats.to_verify} informazioni rimanenti`
                  : "Invia al consulente"}
              </Button>
            )}
          </div>
          <div className="h-2 rounded-full bg-white mt-3" role="progressbar"
            aria-valuenow={stats.completion_percentage} aria-valuemin={0} aria-valuemax={100}
            data-testid="extq-progress-bar">
            <div className="h-full rounded-full bg-green-700 transition-all"
              style={{ width: `${stats.completion_percentage}%` }} />
          </div>
        </CardContent>
      </Card>

      {consultantView && ["SUBMITTED_BY_COMPANY", "UNDER_CONSULTANT_REVIEW",
        "READY_FOR_APPROVAL"].includes(inst.status) && (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="consultant-review-bar">
          <CardContent className="p-4 flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-800 mr-2">Revisione consulente:</p>
            {inst.status === "SUBMITTED_BY_COMPANY" && (
              <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
                onClick={() => workflow("START_REVIEW")} data-testid="start-review-btn">
                Avvia revisione
              </Button>
            )}
            <Button size="sm" variant="outline"
              className="h-8 text-xs text-amber-700 border-amber-300" disabled={busy}
              onClick={() => setShowIntegration(!showIntegration)}
              data-testid="request-integration-btn">
              <MessageSquarePlus className="w-3 h-3 mr-1" aria-hidden="true" />Richiedi integrazioni
            </Button>
            {inst.status === "UNDER_CONSULTANT_REVIEW" && (
              <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
                onClick={() => workflow("READY_FOR_APPROVAL")} data-testid="ready-approval-btn">
                Pronto per approvazione
              </Button>
            )}
            {canApprove && (
              <Button size="sm" className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
                disabled={busy || stats.conflicts > 0 || stats.required_missing > 0}
                onClick={() => workflow("approve")} data-testid="approve-extq-btn"
                title={stats.conflicts ? "Risolvi prima i conflitti" : "Approva e blocca"}>
                <FileCheck className="w-3 h-3 mr-1" aria-hidden="true" />Approva questionario
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {showIntegration && (
        <Card className="border-amber-200" data-testid="integration-form">
          <CardContent className="p-4 space-y-2">
            <p className="text-xs font-semibold text-amber-900">
              Richiesta operativa per l'azienda (vedrà solo questo testo)
            </p>
            <div className="flex gap-2">
              <input type="text" value={integrationComment}
                onChange={(e) => setIntegrationComment(e.target.value)}
                placeholder="Es. Confermare il numero dipendenti e caricare evidenza" maxLength={1000}
                className="flex-1 h-9 rounded-md border border-slate-300 px-3 text-sm"
                data-testid="integration-comment-input" aria-label="Commento per l'azienda" />
              <Button size="sm" className="h-9 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                disabled={busy || !integrationComment.trim()}
                onClick={() => workflow("REQUEST_INTEGRATION")}
                data-testid="integration-send-btn">Invia</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {consultantView && ["APPROVED", "EXPORTED"].includes(inst.status) && (
        <Card className="border-slate-200" data-testid="export-bar">
          <CardContent className="p-4 flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-800 mr-2">Export (solo consulente):</p>
            {["PDF", "XLSX", "CSV"].map((f) => (
              <span key={f} className="inline-flex gap-1">
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => doExport(f, false)} data-testid={`export-draft-${f}`}>
                  <Download className="w-3 h-3 mr-1" aria-hidden="true" />{f} bozza
                </Button>
                <Button size="sm" className="h-8 text-xs bg-slate-800 hover:bg-slate-900 text-white"
                  onClick={() => doExport(f, true)} data-testid={`export-final-${f}`}>
                  {f} finale
                </Button>
              </span>
            ))}
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
              onClick={() => workflow("new-version")} data-testid="new-version-extq-btn">
              Nuova versione (dati aggiornati)
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">
          {focusMode ? `Da completare (${focusQuestions.length})` : `Tutte le domande (${questions.length})`}
        </h2>
        <Button variant="outline" size="sm" className="h-8 text-xs"
          onClick={() => setFocusMode(!focusMode)} data-testid="focus-mode-toggle">
          {focusMode ? "Vedi tutto" : "Solo ciò che manca"}
        </Button>
      </div>

      {(template.sections || []).map((sec) => {
        const secQuestions = (focusMode ? focusQuestions : questions)
          .filter((q) => q.section === sec.code);
        if (!secQuestions.length) return null;
        const open = openSections[sec.code] !== false;
        return (
          <div key={sec.code} className="space-y-3" data-testid={`section-${sec.code}`}>
            <button type="button"
              className="flex items-center gap-2 text-sm font-bold text-slate-800 hover:text-green-800"
              onClick={() => setOpenSections({ ...openSections, [sec.code]: !open })}
              data-testid={`section-toggle-${sec.code}`}>
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              {sec.title}
              <span className="text-xs font-normal text-slate-500">({secQuestions.length})</span>
            </button>
            {open && secQuestions.map((q) => (
              <QuestionCard key={q.qid} question={q} answer={answersByQ[q.qid]}
                editable={clientEditable || consultantEditable}
                consultantView={consultantView} onAction={action} busy={busy} />
            ))}
          </div>
        );
      })}

      {focusMode && focusQuestions.length === 0 && (
        <Card className="border-green-200 bg-green-50" data-testid="all-done-card">
          <CardContent className="p-6 text-center">
            <CheckCircle2 className="w-8 h-8 text-green-700 mx-auto mb-2" aria-hidden="true" />
            <p className="text-sm font-semibold text-green-900">
              Tutto compilato: non resta nulla da completare.
            </p>
          </CardContent>
        </Card>
      )}

      <p className="text-[11px] text-slate-400 flex items-center gap-1">
        <Info className="w-3 h-3" aria-hidden="true" />
        Le risposte vengono salvate automaticamente. Puoi interrompere e riprendere in qualsiasi momento.
      </p>
    </div>
  );
}
